import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  BankTransactionParticipation,
  ContributionPlanWithMembersModel,
  CreateBankTransactionModel,
  TransactionType,
  BankMutation,
} from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import SplitMembersList from '../financial/SplitMembersList';
import Slider from '../misc/Slider';
import DropdownSelect from '../forms/DropdownSelect';
import * as bankAccountService from '../../../services/community/bankAccountService';

interface AssignSplitTransactionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  transaction: CreateBankTransactionModel;
  mutation?: BankMutation;
  onAssignSuccess: (transaction: CreateBankTransactionModel) => void;
  currentIndex?: number;
  totalCount?: number;
}

type AssignmentType = 'ENTIRE_GROUP' | 'INDIVIDUALS';

type ContributionType = 'YES' | 'NO';

type BankTransactionState = {
  description: string;
  date: Date;
  amount: number;
  transactionType?: TransactionType;
  selectedMembers: number[];
  memberValues: Record<number, number>;
  memberRations?: Record<number, number | undefined>;
  assignmentType?: AssignmentType;
  isContributionPayment?: ContributionType;
  contributionPayment?: { dashboardMemberId?: number; contributionPlanId?: number };
};

const getEmptyTransactionState = (
  mutation?: BankMutation,
  initialTransaction?: CreateBankTransactionModel,
) => {
  const transactionInfo = mutation || initialTransaction;
  const hasContribution = !!initialTransaction?.contributionPayment?.dashboardMemberId && !!initialTransaction?.contributionPayment?.contributionPlanId;
  return {
    description: initialTransaction?.description || mutation?.description || '',
    date: initialTransaction?.date ? new Date(initialTransaction.date) : new Date(),
    amount: transactionInfo?.amount ?? 0,
    selectedMembers: [],
    memberValues: {},
    memberRations: {},
    assignmentType: 'ENTIRE_GROUP' as AssignmentType,
    isContributionPayment: (hasContribution ? 'YES' : 'NO') as ContributionType,
    contributionPayment: hasContribution
      ? {
          dashboardMemberId: initialTransaction!.contributionPayment!.dashboardMemberId,
          contributionPlanId: initialTransaction!.contributionPayment!.contributionPlanId,
        }
      : undefined,
  };
};

export default function AssignSplitTransactionModal({
  open,
  setOpen,
  transaction: initialTransaction,
  mutation,
  onAssignSuccess,
  currentIndex,
  totalCount,
}: AssignSplitTransactionModalProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);

  const [transaction, setTransaction] = useState<BankTransactionState>(
    getEmptyTransactionState(mutation, initialTransaction),
  );

  // Filter connections to only show community members
  const communityMembers = connections.filter(
    (connection) => connection.type === ConnectionType.MEMBER || connection.type === ConnectionType.EXTERNAL_MEMBER,
  );

  const [plans, setPlans] = useState<Record<string, ContributionPlanWithMembersModel>>({});

  useEffect(() => {
    if (open) {
      bankAccountService.getAllContributionPlans().then((ps) => {
        setPlans(_.keyBy(ps, (p) => String(p.contributionPlanId)));
      });
    }
  }, [open]);

  const getContributionPlansForConnection = useMemo(() => {
    return (dashboardMemberId: number) =>
      Object.values(plans).filter((plan) =>
        plan.appliesTo.some((member) => member.dashboardMemberId === dashboardMemberId),
      );
  }, [plans]);

  const connectionsWithPlans = useMemo(
    () =>
      communityMembers.filter((connection) =>
        Object.values(plans).some((plan) =>
          plan.appliesTo.some(
            (member) => member.dashboardMemberId === +connection.dashboardMemberId,
          ),
        ),
      ),
    [communityMembers, plans],
  );

  // Auto-select contribution plan if the selected member has exactly one plan
  useEffect(() => {
    if (
      transaction.contributionPayment?.dashboardMemberId &&
      transaction.contributionPayment?.contributionPlanId === undefined
    ) {
      const availablePlans = getContributionPlansForConnection(
        transaction.contributionPayment.dashboardMemberId,
      );
      if (availablePlans.length === 1) {
        setTransaction((prev) => ({
          ...prev,
          contributionPayment: {
            ...prev.contributionPayment!,
            contributionPlanId: availablePlans[0].contributionPlanId,
          },
        }));
      }
    }
  }, [
    transaction.contributionPayment?.dashboardMemberId,
    transaction.contributionPayment?.contributionPlanId,
    getContributionPlansForConnection,
  ]);

  // Calculate remaining amount (sign-agnostic)
  const remainingAmount = useMemo(() => {
    if (transaction.assignmentType === 'ENTIRE_GROUP') return 0;
    const totalAssignedAbs = Object.values(transaction.memberValues).reduce(
      (sum, value) => sum + Math.abs(value || 0),
      0,
    );
    return Math.abs(transaction.amount) - totalAssignedAbs;
  }, [transaction.assignmentType, transaction.memberValues, transaction.amount]);

  // Handle assignment type change
  const handleAssignmentTypeChange = (assignmentType: AssignmentType) => {
    setTransaction((prev) => ({
      ...prev,
      assignmentType,
      selectedMembers: [],
      memberValues: {},
      ...(assignmentType === 'ENTIRE_GROUP'
        ? { isContributionPayment: 'NO' as ContributionType, contributionPayment: undefined }
        : { isContributionPayment: prev.isContributionPayment ?? 'NO', contributionPayment: undefined }),
    }));
  };

  const handleContributionTypeChange = (isContributionPayment: ContributionType) => {
    setTransaction((prev) => ({
      ...prev,
      isContributionPayment,
      contributionPayment: isContributionPayment === 'NO' ? undefined : prev.contributionPayment,
    }));
  };

  // Member selection removed in favor of always-visible rows

  // Select all replaced by Split/Reset button

  // Rations recalculation (proportional on remaining)
  const recalcFromRations = (
    prev: BankTransactionState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): BankTransactionState => {
    const { amount } = prev;
    const totalAbs = Math.abs(amount);
    const memberValues = override?.memberValues ?? prev.memberValues ?? {};
    const memberRations = override?.memberRations ?? prev.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };
    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return { ...prev, memberValues: newMemberValues, memberRations };
  };

  // Handle member value change (clamped, sets member to manual)
  const handleValueChange = (memberId: number, value: number) => {
    setTransaction((prev) => {
      const valueInCentsAbs = Math.max(0, Math.floor((value || 0) * 100));
      const memberRations = { ...(prev.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prev, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(prev.amount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, valueInCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: clampedAbs,
      };

      return recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
    });
  };

  // Current amount formatting handled by SplitMembersList

  // Handle save
  const handleSave = () => {
    if (
      transaction.assignmentType === 'INDIVIDUALS' &&
      transaction.isContributionPayment === 'YES'
    ) {
      const dashboardMemberId = transaction.contributionPayment?.dashboardMemberId;
      const contributionPlanId = transaction.contributionPayment?.contributionPlanId;
      if (dashboardMemberId === undefined || contributionPlanId === undefined) return;

      const updatedTransaction: CreateBankTransactionModel = {
        ...initialTransaction,
        description: transaction.description,
        date: transaction.date,
        amount: transaction.amount,
        participations: undefined,
        transactionType: TransactionType.CONTRIBUTION,
        contributionPayment: { dashboardMemberId, contributionPlanId },
      };
      onAssignSuccess(updatedTransaction);
      return;
    }

    let participations: BankTransactionParticipation[] | undefined;

    if (transaction.assignmentType === 'INDIVIDUALS') {
      const sign = transaction.amount >= 0 ? 1 : -1;
      const participatingMembers = Object.entries(transaction.memberValues)
        .filter(([, value]) => (value || 0) > 0)
        .map(([id]) => +id);
      if (participatingMembers.length === 0) return;

      participations = participatingMembers.map((memberId) => ({
        dashboardMemberId: memberId,
        amount: sign * (transaction.memberValues[memberId] || 0),
      }));
    }

    const updatedTransaction: CreateBankTransactionModel = {
      ...initialTransaction,
      description: transaction.description,
      date: transaction.date,
      amount: transaction.amount,
      participations,
      transactionType:
        transaction.assignmentType === 'ENTIRE_GROUP'
          ? transaction.amount >= 0
            ? TransactionType.GROUP_INCOME
            : TransactionType.GROUP_EXPENSE
          : transaction.amount >= 0
            ? TransactionType.PERSONAL_INCOME
            : TransactionType.PERSONAL_EXPENSE,
    };

    onAssignSuccess(updatedTransaction);
  };

  // Split equally among all community members
  const splitEqually = () => {
    setTransaction((prev) => {
      const selected = communityMembers.map((m) => +m.dashboardMemberId);
      if (selected.length === 0) return prev;
      const totalAbs = Math.abs(prev.amount);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;
      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = equalAbs + (index < remainder ? 1 : 0);
        memberRations[id] = undefined;
      });
      return { ...prev, selectedMembers: selected, memberValues, memberRations };
    });
  };

  // Reset all selections and values
  const resetSplit = () => {
    setTransaction((prev) => ({
      ...prev,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(transaction.memberValues).some((v) => (v || 0) > 0),
    [transaction.memberValues],
  );

  // Initialize transaction state when modal opens
  useEffect(() => {
    if (open) {
      setTransaction(getEmptyTransactionState(mutation, initialTransaction));
    }
  }, [open, mutation, initialTransaction]);

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="rounded-md bg-secondary-50 p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold">
            {t('page.financial.splitModal.assignSplitTransaction')}
            {currentIndex !== undefined && totalCount !== undefined && totalCount > 1 && (
              <span className="ml-2 text-base font-normal text-gray-500">
                ({currentIndex}/{totalCount})
              </span>
            )}
          </h3>
        </div>

        {/* Transaction Info Card - Same style as TransactionModal */}
        <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t('page.financial.splitModal.date')}
                </p>
                <p className="text-sm font-semibold">{formatDate(transaction.date)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t('page.financial.splitModal.amount')}
                </p>
                <p
                  className={twMerge(
                    'text-sm font-semibold',
                    transaction.amount > 0 ? 'text-success' : 'text-danger',
                  )}>
                  {formatEuroCentsWithSign(transaction.amount, i18n.language)}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">
                {t('page.financial.splitModal.description')}
              </p>
              <p className="max-w-md truncate text-sm font-semibold" title={transaction.description}>
                {transaction.description || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Assign Flow */}
        <div className="mb-4">
          <div className="mb-2 flex flex-row gap-4">
            <div className="flex-1">
              <Labeled
                label={t('page.financial.splitModal.description')}
                htmlFor="transaction-description-textarea">
                <textarea
                  className="h-[unset] w-full"
                  rows={2}
                  id="transaction-description-textarea"
                  placeholder={t('page.financial.splitModal.descriptionPlaceholder')}
                  value={transaction.description}
                  onChange={(ev) =>
                    setTransaction((prevTns) => ({
                      ...prevTns,
                      description: ev.target.value,
                    }))
                  }
                />
              </Labeled>
            </div>
          </div>

          {/* Assignment Type Selection */}
          <div className="mb-6">
            <Slider
              label={t('page.financial.splitModal.assignmentTypeLabel', {
                type: transaction.amount < 0 ? 'expense' : 'income',
              })}
              items={[
                {
                  label: t('page.financial.splitModal.entireGroup'),
                  value: 'ENTIRE_GROUP' as AssignmentType,
                },
                {
                  label: t('page.financial.splitModal.individuals'),
                  value: 'INDIVIDUALS' as AssignmentType,
                },
              ]}
              value={transaction.assignmentType}
              onChange={handleAssignmentTypeChange}
            />
          </div>

          {/* Contribution payment toggle (income + individuals + plans exist) */}
          {transaction.assignmentType === 'INDIVIDUALS' &&
            transaction.amount > 0 &&
            Object.keys(plans).length > 0 && (
              <div className="mb-6">
                <Slider
                  label={t('page.financial.splitModal.contributionPaymentQuestion')}
                  items={[
                    { label: t('page.financial.splitModal.no'), value: 'NO' as ContributionType },
                    { label: t('page.financial.splitModal.yes'), value: 'YES' as ContributionType },
                  ]}
                  value={transaction.isContributionPayment ?? 'NO'}
                  onChange={handleContributionTypeChange}
                />
              </div>
            )}

          {transaction.assignmentType === 'INDIVIDUALS' &&
            transaction.isContributionPayment === 'YES' && (
              <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                <div className="space-y-4">
                  <Labeled label={t('page.financial.splitModal.member')}>
                    <DropdownSelect
                      options={connectionsWithPlans.map((c) => ({
                        label: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
                        value: +c.dashboardMemberId,
                      }))}
                      setValue={(dashboardMemberId) =>
                        setTransaction((prev) => ({
                          ...prev,
                          contributionPayment: {
                            dashboardMemberId,
                            contributionPlanId: undefined,
                          },
                        }))
                      }
                      value={transaction.contributionPayment?.dashboardMemberId}
                    />
                  </Labeled>
                  {transaction.contributionPayment?.dashboardMemberId && (
                    <Labeled label={t('page.financial.splitModal.plan')}>
                      <DropdownSelect
                        options={getContributionPlansForConnection(
                          transaction.contributionPayment.dashboardMemberId,
                        ).map((p) => ({
                          label: p.name,
                          value: p.contributionPlanId,
                        }))}
                        setValue={(contributionPlanId) =>
                          setTransaction((prev) => ({
                            ...prev,
                            contributionPayment: {
                              ...prev.contributionPayment!,
                              contributionPlanId,
                            },
                          }))
                        }
                        value={transaction.contributionPayment?.contributionPlanId}
                      />
                    </Labeled>
                  )}
                </div>
              </div>
            )}

          {transaction.assignmentType === 'INDIVIDUALS' &&
            transaction.isContributionPayment !== 'YES' && (
              <SplitMembersList
                members={communityMembers.map((m) => ({
                  id: +m.dashboardMemberId,
                  label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
                }))}
                memberValues={transaction.memberValues}
                memberRations={transaction.memberRations}
                onSetValue={(id, v) => handleValueChange(id, v)}
                onSetRations={(id, r) =>
                  setTransaction((prev) =>
                    recalcFromRations(prev, {
                      memberRations: { ...(prev.memberRations ?? {}), [id]: r },
                    }),
                  )
                }
                headerTitle={t('page.financial.splitModal.selectMembers')}
                onSplit={splitEqually}
                onClear={resetSplit}
                isResetable={isResetable}
                remainingAmount={remainingAmount}
                remainingLabel={t('page.financial.splitModal.remaining')}
              />
            )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="primary-dark"
            className="bg-secondary-200 text-primary-900"
            onClick={() => setOpen(false)}>
            {t('page.financial.splitModal.cancelButton')}
          </Button>

          <Button
            variant="primary-dark"
            onClick={handleSave}
            disabled={
              !transaction.assignmentType ||
              !transaction.amount ||
              (transaction.assignmentType === 'INDIVIDUALS' &&
                transaction.isContributionPayment !== 'YES' &&
                (Math.abs(remainingAmount) > 1 ||
                  Object.values(transaction.memberValues).every((v) => (v || 0) === 0))) ||
              (transaction.assignmentType === 'INDIVIDUALS' &&
                transaction.isContributionPayment === 'YES' &&
                (!transaction.contributionPayment?.dashboardMemberId ||
                  !transaction.contributionPayment?.contributionPlanId))
            }>
            {t('page.financial.splitModal.assignButton')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
